/**
 * Email identity rules for Google Workspace sign-in.
 *
 * **`innoctive.com` is the only login domain.** `cargofl.com` exists as a Workspace
 * alias and is used as the SES *sending* address (`no-reply@cargofl.com`), but it is
 * deliberately NOT accepted for sign-in and no employee email is stored on it.
 * Do not "helpfully" widen this: allowing both domains without canonicalising them
 * would give one person two employee records, each with their own goals and
 * assessments, and the split would surface only when an appraisal went missing.
 *
 * Email is the human-readable key. The strongest anchor is Google's `sub` claim,
 * which survives a rename - match on that first and treat email as the fallback.
 */

/** The only domain permitted to sign in, and the domain every employee email uses. */
export const ALLOWED_EMAIL_DOMAIN = 'innoctive.com';

/** Lowercased domain part, or null if this is not a single-@ address. */
export function emailDomain(email: string): string | null {
  const parts = email.trim().toLowerCase().split('@');
  if (parts.length !== 2) return null;
  const domain = parts[1];
  return domain && domain.length > 0 ? domain : null;
}

export function emailLocalPart(email: string): string | null {
  const parts = email.trim().toLowerCase().split('@');
  if (parts.length !== 2) return null;
  const local = parts[0];
  return local && local.length > 0 ? local : null;
}

/**
 * The address an admin means when they type a name into an email box whose
 * `@innoctive.com` is fixed: everything from an "@" on is dropped (so a pasted
 * `mubin@gmail.com` becomes `mubin@innoctive.com`), whitespace removed, and an
 * empty name gives "" rather than a bare "@innoctive.com".
 */
export function companyEmail(typed: string, domain: string = ALLOWED_EMAIL_DOMAIN): string {
  const name = (typed.split('@')[0] ?? '').replace(/\s+/g, '');
  return name ? `${name}@${domain}` : '';
}

/**
 * Whether this address may sign in.
 *
 * The API applies this to the ID token's `email` claim AND checks `hd` separately -
 * neither alone is sufficient.
 */
export function isAllowedEmailDomain(
  email: string,
  allowedDomain: string = ALLOWED_EMAIL_DOMAIN,
): boolean {
  // The local part must be checked too, not just the domain: "@innoctive.com" has
  // a valid domain half and no addressee, and would otherwise be accepted.
  if (emailLocalPart(email) === null) return false;
  return emailDomain(email) === allowedDomain.trim().toLowerCase();
}

/**
 * Whether a Google `hd` (hosted domain) claim names the Workspace.
 *
 * A personal Google account carries no `hd` at all, so a missing value must fail.
 */
export function isAllowedHostedDomain(
  hd: string | null | undefined,
  allowedDomain: string = ALLOWED_EMAIL_DOMAIN,
): boolean {
  if (!hd) return false;
  return hd.trim().toLowerCase() === allowedDomain.trim().toLowerCase();
}

/**
 * The single stored form of an address: trimmed and lowercased, with any `+tag`
 * removed.
 *
 * The `+tag` strip matters because Workspace cannot have an account name containing
 * `+`, so such an address is only ever plus-addressing at an existing mailbox -
 * treating it as a new person would create a duplicate employee.
 *
 * Dots are deliberately NOT stripped: unlike consumer Gmail, Workspace treats
 * `a.b@` and `ab@` as different accounts, so collapsing them would merge two real
 * employees into one record.
 */
export function canonicalizeEmail(email: string): string {
  const trimmed = email.trim().toLowerCase();
  const local = emailLocalPart(trimmed);
  const domain = emailDomain(trimmed);
  if (local === null || domain === null) return trimmed;

  const plusIndex = local.indexOf('+');
  const baseLocal = plusIndex === -1 ? local : local.slice(0, plusIndex);
  return `${baseLocal}@${domain}`;
}

/** True when two addresses identify the same person. */
export function isSameIdentity(a: string, b: string): boolean {
  return canonicalizeEmail(a) === canonicalizeEmail(b);
}
